import styled, { css } from 'styled-components'
import { safNodeWidth } from './safUtils/safUtils'
import ScrollBar from '~/components/ScrollBar'

const STOCK_OFFSET_Y = -4

export const Container = styled(ScrollBar)`
  max-width: 100%;
  max-height: 960px;
  height: 100%;
`

export const Content = styled.div`
  display: flex;
  padding: 200px 0 16px;
  position: relative;
`

type ColumnProps = {
  columnWidth: number
}

export const ColumnsContainer = styled.div<ColumnProps>`
  background-image: linear-gradient(
    90deg,
    #f6f7f9 0%,
    #f6f7f9 50%,
    #ebf0f4 50%,
    #ebf0f4 100%
  );
  background-position: ${({ columnWidth }) => `${columnWidth / 2}px 0`};
  background-size: ${({ columnWidth }) => `${columnWidth * 2}px`};
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
`

export const ColumnsHeader = styled.div<ColumnProps>`
  background-size: inherit;
  background-position: inherit;
  background-image: linear-gradient(
    90deg,
    #e2e8ee 0%,
    #e2e8ee 50%,
    #d8e1ea 50%,
    #d8e1ea 100%
  );
  min-height: 2.75rem;
  padding: 0.5rem 0.5rem 0.5rem 0;
  display: grid;
  grid-template-columns: ${({ columnWidth }) => `repeat(
      auto-fill,
      ${columnWidth / 2}px
    )`};
  text-align: center;
  place-items: center flex-end;
  grid-auto-flow: column;
  overflow: hidden;

  > * {
    transform: translateX(50%);
  }
`

export const ChartContainer = styled.div<ColumnProps>`
  position: relative;
  z-index: 1;
  margin-left: ${({ theme, columnWidth }) =>
    `${columnWidth / 2 - safNodeWidth(theme.saf) / 2}px`};
`

export const LinkLabel = styled.div`
  display: flex;
  position: absolute;
  transform: translate3d(-50%, -50%, 0);
  border: 1px solid #c9c9cb;
  border-radius: 6px;
  background: #fff;
  padding: 8px;
`

export const NodeBox = styled.div`
  display: flex;
  position: absolute;
  width: ${({ theme }) => `${safNodeWidth(theme.saf)}px`};
  min-height: 8px;
`

export const NodeLabel = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  text-align: center;
  transform: translateY(-100%);
  padding-bottom: ${({ theme }) => `${theme.saf.pillTipHeight + 8}px`};
  margin-left: -100%;
  width: 203px;
`

export const NodePillsWrapper = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  gap: 8px;
`

export const NodePill = styled.div<{
  isSource?: boolean
  sideNumber?: boolean
}>`
  height: ${({ theme }) =>
    `calc(var(--baseHeight) - ${theme.saf.pillTipHeight}px)`};
  width: ${({ theme }) => `${theme.saf.pillWidth}px`};
  display: flex;
  color: #fff;
  align-items: center;
  flex-direction: column;
  position: relative;
  border: 1px solid;
  border-top-width: 0;
  top: ${({ theme }) => `${theme.saf.pillTipHeight}px`};

  ::before {
    position: absolute;
    top: ${({ theme }) => `-${theme.saf.pillTipHeight}px`};
    display: block;
    content: '';
    border-radius: ${({ theme }) =>
      `${theme.saf.pillTipHeight}px ${theme.saf.pillTipHeight}px 0 0`};
    width: ${({ theme }) => `${theme.saf.pillWidth}px`};
    height: ${({ theme }) => `${theme.saf.pillTipHeight}px`};
    background: inherit;
    z-index: -1;
    border: 1px solid;
    border-color: inherit;
    border-bottom-width: 0;
  }

  ${({ isSource }) =>
    isSource &&
    css`
      margin-left: auto;
      top: 0;
      border-top-width: 1px;
      border-bottom-width: 0;

      ::before {
        transform: scaleY(-100%);
        top: unset;
        bottom: ${({ theme }) => `-${theme.saf.pillTipHeight}px`};
      }
    `}

  > :first-child {
    ${({ sideNumber, isSource, theme }) =>
      isSource &&
      sideNumber &&
      css`
        transform: translateY(${theme.saf.pillTipHeight}px);
      `}
  }

  > :last-child {
    ${({ sideNumber, isSource }) =>
      sideNumber &&
      (isSource
        ? css`
            position: absolute;
            bottom: ${({ theme }) => `${4 - theme.saf.pillTipHeight}px`};
            right: -2px;
            transform: translateX(100%);
          `
        : css`
            position: absolute;
            bottom: 4px;
            left: -2px;
            transform: translateX(-100%);
          `)}
  }
`

export const NodeValue = styled.div<{ inValue?: number }>`
  display: flex;
  height: ${({ theme }) => `calc(100% + ${theme.saf.pillTipHeight}px)`};
  align-items: center;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  top: ${({ theme, inValue }) =>
    inValue ? `-${theme.saf.pillTipHeight}px` : '0'};
`

type NodeStockProps = {
  inValue: number
  outValue: number
  remainingStock: number
  isSource?: boolean
  sideNumber?: boolean
}

const nodeStockPosition = ({
  inValue,
  outValue,
  remainingStock,
  isSource
}: NodeStockProps) => {
  if (outValue + remainingStock < inValue) {
    return css`
      bottom: ${STOCK_OFFSET_Y}px;
    `
  }

  const calcValue = `${(-100 * remainingStock) / outValue}% + ${STOCK_OFFSET_Y}px`
  if (outValue > inValue) {
    return css`
      bottom: calc(${calcValue});
    `
  }

  if (outValue < inValue && isSource) {
    return css`
      top: calc(${(100 * outValue) / inValue}% - ${STOCK_OFFSET_Y}px);
    `
  }

  const percentage = (-100 * (outValue + remainingStock - inValue)) / inValue
  return css`
    bottom: calc(${percentage}% + ${STOCK_OFFSET_Y}px);
  `
}

export const NodeStock = styled.div<NodeStockProps>`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  height: 0;
  width: ${({ theme }) => `${theme.saf.pillWidth}px`};
  color: #303032;
  border: 1px solid;

  > * {
    transform: translateY(100%);
  }

  ${nodeStockPosition}

  ${({ isSource, outValue, inValue, remainingStock }) =>
    isSource &&
    css`
      right: 0;
      margin-left: auto;
      height: ${inValue && outValue && remainingStock
        ? `${(100 * remainingStock) / Math.max(inValue, outValue)}%`
        : undefined};
    `}
  ${({ sideNumber, isSource }) =>
    sideNumber &&
    (isSource
      ? css`
          justify-content: flex-end;

          > * {
            transform: translate3d(100%, 3px, 0);
            margin-right: -2px;
          }
        `
      : css`
          justify-content: flex-start;

          > * {
            transform: translate3d(-100%, 3px, 0);
            margin-left: -2px;
          }
        `)}
`

export const StockArrowSvg = styled.svg.attrs(({ theme }) => ({
  width: theme.saf.pillsGap,
  height: '100%'
}))`
  position: absolute;
  z-index: 1;
  bottom: ${STOCK_OFFSET_Y}px;
  left: ${({ theme }) => `${theme.saf.pillWidth}px`};
  overflow: visible;
`

export const TotalsContainer = styled.div`
  position: relative;
  margin-top: 100px;
`
